import { AlertTriangle, Inbox, RefreshCw, WifiOff } from 'lucide-react';
import type { ReactNode } from 'react';

import { ErreurApi } from '@/shared/api/erreurs';
import { Bouton } from '@/shared/ui/Bouton';
import { Squelette } from '@/shared/ui/Surface';
import { cn } from '@/shared/lib/utils';

/**
 * Etats standardises de chargement, d'erreur et de vide (§4).
 *
 * Toute vue qui interroge l'API passe par ces trois composants. C'est ce qui
 * garantit qu'aucun ecran ne reste blanc — ni pendant le chargement, ni apres un
 * echec reseau, ni quand il n'y a simplement rien a montrer.
 */

export function EtatVide({
  titre,
  description,
  action,
  icone: Icone = Inbox,
  className,
}: {
  titre: string;
  description?: string;
  action?: ReactNode;
  icone?: typeof Inbox;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-[--radius-astro-lg]',
        'border border-dashed border-bordure px-6 py-14 text-center',
        className,
      )}
    >
      <div className="flex size-11 items-center justify-center rounded-full bg-surface-haute">
        <Icone className="size-5 text-texte-faible" />
      </div>

      <div className="space-y-1">
        <p className="font-medium text-texte">{titre}</p>
        {description ? (
          <p className="max-w-sm text-sm text-texte-doux">{description}</p>
        ) : null}
      </div>

      {action}
    </div>
  );
}

/**
 * Etat d'erreur.
 *
 * Le bouton « Reessayer » n'apparait que si reessayer a une chance d'aboutir :
 * proposer de rejouer un 403 ou un 409 revient a promettre un resultat different
 * pour la meme action, ce qui est faux et agacant.
 */
export function EtatErreur({
  erreur,
  onReessayer,
  className,
}: {
  erreur: unknown;
  onReessayer?: () => void;
  className?: string;
}) {
  const estApi = erreur instanceof ErreurApi;
  const transitoire = estApi ? erreur.estTransitoire : true;
  const reseau = estApi && erreur.nature === 'reseau';

  const message = estApi
    ? erreur.message
    : erreur instanceof Error
      ? erreur.message
      : 'Une erreur inattendue est survenue.';

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-[--radius-astro-lg]',
        'border border-danger/30 bg-danger-voile px-6 py-12 text-center',
        className,
      )}
    >
      <div className="flex size-11 items-center justify-center rounded-full bg-danger/15">
        {reseau ? (
          <WifiOff className="size-5 text-danger" />
        ) : (
          <AlertTriangle className="size-5 text-danger" />
        )}
      </div>

      <div className="space-y-1">
        <p className="font-medium text-texte">{message}</p>

        {estApi && erreur.correlationId ? (
          <p className="font-mono text-xs text-texte-faible">
            Reference : {erreur.correlationId}
          </p>
        ) : null}
      </div>

      {transitoire && onReessayer ? (
        <Bouton variante="secondaire" taille="sm" onClick={onReessayer}>
          <RefreshCw />
          Reessayer
        </Bouton>
      ) : null}
    </div>
  );
}

/**
 * Squelette de tableau.
 *
 * Les hauteurs correspondent a celles des vraies lignes (`h-10` d'en-tete,
 * `py-3` de cellule). Le contenu ne saute donc pas a l'arrivee des donnees :
 * c'est la condition du CLS a zero exigee au §7.
 */
export function SqueletteTableau({ lignes = 6, colonnes = 4 }: { lignes?: number; colonnes?: number }) {
  return (
    <div className="w-full space-y-px" aria-hidden>
      <div className="flex h-10 items-center gap-3 border-b border-bordure px-3">
        {Array.from({ length: colonnes }, (_, index) => (
          <Squelette key={index} className="h-3 flex-1" />
        ))}
      </div>

      {Array.from({ length: lignes }, (_, ligne) => (
        <div key={ligne} className="flex items-center gap-3 border-b border-bordure px-3 py-3">
          {Array.from({ length: colonnes }, (_, colonne) => (
            <Squelette key={colonne} className="h-4 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function SqueletteCarte({ className }: { className?: string }) {
  return (
    <div
      className={cn('space-y-3 rounded-[--radius-astro-lg] border border-bordure p-5', className)}
      aria-hidden
    >
      <Squelette className="h-4 w-1/3" />
      <Squelette className="h-3 w-2/3" />
      <Squelette className="h-3 w-1/2" />
    </div>
  );
}
